/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Trans, Translation } from 'react-i18next'
import { default_theme as theme } from '../emotion/theme'
import { join, split, map, get, size, filter } from 'lodash'
import { confirmModal } from '../actions/ui'
import { topupList } from '../actions/topup'
import { FreeMinutesDuration } from './FreeMinutesDuration'
import JSONPretty from 'react-json-pretty'
import { PhoneNumberRedirectServerSyncLog } from './PhoneNumberRedirectServerSyncLog'
import 'react-json-pretty/themes/monikai.css'
import { showSuccess } from '../actions/Error'
import { Error } from './layout/Error'
import { phoneNumberRedirectServerSyncLogList } from '../actions/phone_number_redirect_server_sync_log'
import { blockedNumberAutoCompleteList } from '../actions/blocked_number_auto_complete'
import { callForwardingCostCalculatorList } from '../actions/call_forwarding_cost_calculator'
import { NoodleHead, NoodleStem, NoodleTail, NoodleBendRight } from './layout/Noodles'
import { CardHeader } from './layout/CardHeader'
import { css } from '@emotion/react'
import { FormikInputField } from './form/InputField'
import { InlineInputField } from './form/InlineInputField'
import { handleSubmitResult } from '../actions/form'
import { answeringMessageVoiceMessageList, answeringMessageIntroMessageList, answeringMessageGoodbyeMessageList } from '../actions/answering_message'
import { Row, Col } from 'react-bootstrap'
import { globalSettingsList } from '../actions/settings'
import { Separator } from './layout/Separator'
import { customerPhoneNumberForwardingList } from '../actions/customer_phone_number_forwarding'
import Card from './layout/Card'
import WrappingBusyMask from './WrappingBusyMask'
import { PhoneNumber } from './PhoneNumber'
import { VoiceCallPlanOfficeHoursRow } from './VoiceCallPlanOfficeHoursRow'
import { VoiceCallPlanForwardingRow } from './VoiceCallPlanForwardingRow'
import { VoiceCallPlanIntroMessageRow } from './VoiceCallPlanIntroMessageRow'
import { VoiceCallPlanGoodbyeMessageRow } from './VoiceCallPlanGoodbyeMessageRow'
import { VoiceCallPlanVoiceMailRow } from './VoiceCallPlanVoiceMailRow'
import { VoiceCallPlanBlockedNumbersRow } from './VoiceCallPlanBlockedNumbersRow'
import { blockedNumberList } from '../actions/blocked_number'
import { subscriptionVoiceList } from '../actions/customer_subscription'
import { customerPhoneNumberList } from '../actions/customer_phone_number'
import { customerList } from '../actions/customer'
import * as Yup from 'yup'

const phoneNumberNameValidationSchema = Yup.object().shape({
  phone_number_name: Yup.string().required('Required'),
})

// moved email validation into VoiceCallPlanVoiceMailRow

const PHONE_NUMBER_VALIDATION_DEBOUNCE_MILLISECONDS = 300

export const VoiceNumberCallPlan = ({
  subscription_id,
  editing_as_customer,
  overrideAnsweringVoiceMessageList,
  overrideAnsweringIntroMessageList,
  overrideAnsweringGoodbyeMessageList,
  ...props
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const is_loading = useSelector(() =>
    subscriptionVoiceList.isWaitingForObjectLoad()
  )
  const is_saving = useSelector(
    () =>
      customerPhoneNumberList.getIsSavingObject() ||
      customerPhoneNumberList.getIsSavingObject()
  )
  const subscription = useSelector(() =>
    subscriptionVoiceList.getObject(subscription_id)
  )
  const customer_phone_number = useSelector(() =>
    customerPhoneNumberList.getObject(
      get(subscription, ['customer_phone_number', 'id'])
    )
  )
  const phone_number_name_initial_values = customer_phone_number
  const customer = useSelector(() => customerList.getCustomer())
  const helpdesk_email = useSelector(() =>
    globalSettingsList.getSetting('helpdesk_email')
  )
  const helpdesk_phone = useSelector(() =>
    globalSettingsList.getSetting('helpdesk_phone')
  )
  const [greetingSelectorVisible, setGreetingSelectorVisible] = useState(false)
  const [savingMode, setSavingMode] = useState(null)
  const [backdoorShowRoutingJson, setBackdoorShowRoutingJson] = useState(false)
  const answeringMessages = useSelector(() =>
    answeringMessageVoiceMessageList.getVisibleObjects()
  )
  const [forwardingPhoneNumberInfos, setForwardingPhoneNumberInfos] = useState(
    {}
  )
  const [forwardingPhoneNumberTimers, setForwardingPhoneNumberTimers] =
    useState({})
  const blockedNumbersForSubscription = useSelector(() =>
    customerPhoneNumberList.getBlockedNumbers({ customer_phone_number })
  )
  const allBlockedNumbers = useSelector(() =>
    blockedNumberList.getVisibleObjects()
  )
  const all_destinations = useSelector(() =>
    customerPhoneNumberList.getAllDestinations({ customer_phone_number })
  )
  const callForwardingCostsByNumber = useSelector(() =>
    callForwardingCostCalculatorList.getObjectsById()
  ) // this is just to trigger a render when the data arrives
  const office_hours_enabled = useSelector(() =>
    customerPhoneNumberList.getOfficeHoursEnabled({ customer_phone_number })
  )
  const [officeHoursViewMode, setOfficeHoursViewMode] = useState('in_office') // one of OFFICE_HOURS_VIEW_MODE_OPTIONS
  const activeClusterType = office_hours_enabled
    ? officeHoursViewMode
    : 'default'
  const forwardingListTypeOption = useSelector(() =>
    customerPhoneNumberList.getForwardingListTypeOption({
      customer_phone_number,
      cluster_type: activeClusterType,
    })
  )
  const [
    blockedNumberLookupOnDoneCallBack,
    setBlockedNumberLookupOnDoneCallBack,
  ] = useState()
  const blockedNumberAutoCompleteResults = useSelector(() =>
    blockedNumberAutoCompleteList.getResults()
  )

  useEffect(() => {
    dispatch(subscriptionVoiceList.ensureObjectLoaded(subscription_id))
    dispatch(globalSettingsList.ensureGlobalSettingsLoaded())
    dispatch(answeringMessageVoiceMessageList.updatePaginationNumItemsPerPage(100))
    dispatch(answeringMessageVoiceMessageList.updateListFilter({ customer: customer.id, category: "voice_message" }))
    dispatch(answeringMessageVoiceMessageList.fetchListIfNeeded())
    dispatch(blockedNumberList.updatePaginationNumItemsPerPage(1000))
    dispatch(blockedNumberList.fetchListIfNeeded())
  }, [])

  useEffect(() => {
    if (blockedNumberLookupOnDoneCallBack) {
      blockedNumberLookupOnDoneCallBack(blockedNumberAutoCompleteResults)
    }
  }, [JSON.stringify(blockedNumberAutoCompleteResults)])

  useEffect(() => {
    if (get(subscription, ['customer_phone_number', 'id'])) {
      dispatch(
        customerPhoneNumberList.ensureObjectLoaded(
          subscription.customer_phone_number.id
        )
      )
    }
  }, [get(subscription, 'customer_phone_number')])

  useEffect(() => {
    dispatch(
      callForwardingCostCalculatorList.fetchForNumbers({
        subscription_id: subscription_id,
        numbers: map(all_destinations, (x) => x.number),
      })
    )
  }, [JSON.stringify(all_destinations)])

  useEffect(() => {
    if (get(subscription, ['customer'])) {
      dispatch(customerList.ensureObjectLoaded(subscription.customer))
      dispatch(
        answeringMessageVoiceMessageList.updateListFilter({
            customer: subscription.customer,
            category: "voice_message"
        })
      )
      dispatch(answeringMessageVoiceMessageList.fetchListIfNeeded())
        
      dispatch(
        answeringMessageIntroMessageList.updateListFilter({
            customer: subscription.customer,
            category: "intro_message"
        })
      )
      dispatch(answeringMessageIntroMessageList.fetchListIfNeeded())

      dispatch(
        answeringMessageGoodbyeMessageList.updateListFilter({
            customer: subscription.customer,
            category: "goodbye_message"
        })
      )
      dispatch(answeringMessageGoodbyeMessageList.fetchListIfNeeded())
        
    }
  }, [get(subscription, 'customer')])

  useEffect(() => {
    if (!get(customer_phone_number, 'id')) {
      return
    }
    const call_forwarding_enabled = get(
      customer_phone_number,
      'call_forwarding_enabled'
    )
    const voice_mail_enabled = customerPhoneNumberList.getVoiceMailEnabled({
      customer_phone_number,
      cluster_type: activeClusterType,
    })

    if (!call_forwarding_enabled && !voice_mail_enabled) {
      // force voicemail if no call forwarding
      dispatch(onToggleVoiceMailEnabled)
    }
  }, [get(customer_phone_number, 'call_forwarding_enabled')])

  const getActiveClusterType = () => {
    return activeClusterType
  }

  const onBuyCredit = () => {
    topupList.startBuyMoreCredit({ history })
  }

  const onForwardingPhoneNumberBlur = (value, field_name, formik_props) => {
    const timers = Object.assign({}, forwardingPhoneNumberTimers)
    const timer = get(timers, field_name)
    if (timer) {
      window.clearTimeout(timer)
    }
    timers[field_name] = window.setTimeout(() => {
      dispatch(
        customerPhoneNumberForwardingList.checkIsValid({
          number: value,
          customer_subscription: subscription,
        })
      ).then((res) => {
        const newNumberForwardingInfos = Object.assign(
          {},
          forwardingPhoneNumberInfos
        )
        newNumberForwardingInfos[field_name] = res
        setForwardingPhoneNumberInfos(newNumberForwardingInfos)
      })
    }, PHONE_NUMBER_VALIDATION_DEBOUNCE_MILLISECONDS)
    setForwardingPhoneNumberTimers(timers)
  }

  const onForwardingPhoneNumberChanged = (value, field_name, formik_props) => {
    if (forwardingPhoneNumberInfos[field_name]) {
      const newNumberForwardingInfos = Object.assign(
        {},
        forwardingPhoneNumberInfos
      )
      newNumberForwardingInfos[field_name].valid = undefined
      setForwardingPhoneNumberInfos(newNumberForwardingInfos)
    }
  }

  const onSavePhoneNumberName = ({ values, formik_funcs, onSaved }) => {
    setSavingMode('onSavePhoneNumberName')
    commonOnSavePhoneNumber({ values, formik_funcs, onSaved })
  }

  const onSavePhoneNumber = ({ values, formik_funcs, onSaved }) => {
    setSavingMode('onSavePhoneNumber')
    const answering_message = values.answering_message
    if (answering_message) {
      customerPhoneNumberList.setActiveAnsweringMessage({
        customer_phone_number,
        cluster_type: activeClusterType,
        answering_message,
      })
    }
    customerPhoneNumberList.setActiveVoiceForwardingEmail({
      customer_phone_number,
      cluster_type: activeClusterType,
      forward_voicemail_to_email: get(values, 'forward_voicemail_to_email'),
    })
    customerPhoneNumberList.setActiveVoiceForwardingName({
      customer_phone_number,
      cluster_type: activeClusterType,
      forward_voicemail_to_name: get(values, 'forward_voicemail_to_name'),
    })
    commonOnSavePhoneNumber({ formik_funcs, onSaved })
  }

  const onSaveBlockedNumbers = ({ values, formik_funcs, onSaved }) => {
    const newBlockedNumbers = get(values, 'blocked_number')
    setSavingMode('onSaveBlockedNumbers')
    dispatch(
      customerPhoneNumberList.setBlockedNumbers({
        customer_phone_number,
        blocked_numbers: newBlockedNumbers,
      })
    )
    commonOnSavePhoneNumber({ formik_funcs, onSaved })
  }

  const onSaveOfficeHours = ({ values, formik_funcs, onSaved }) => {
    setSavingMode('onSaveOfficeHours')
    customerPhoneNumberList.setOfficeHours({ customer_phone_number, ...values })
    commonOnSavePhoneNumber({ formik_funcs, onSaved })
  }

  const commonOnSavePhoneNumber = useCallback(({
    values,
    formik_funcs,
    onSaved,
    enableSuccessNotification,
  }) => {
    const on_ok = (json) => {
      if (enableSuccessNotification !== false) {
        showSuccess(
          <Trans>Saved</Trans>,
          <Trans>Your change may take a minute to take effect</Trans>
        )
      }
      if (onSaved) {
        onSaved()
      }
      dispatch(
        phoneNumberRedirectServerSyncLogList.reloadForSubscription({
          customer_subscription_id: subscription_id,
        })
      )
    }

    if (!values) {
      values = {
        id: customer_phone_number.id,
        routing_as_json: customer_phone_number.routing_as_json,
      }
    }
    values.id = subscription.customer_phone_number.id

    dispatch(customerPhoneNumberList.saveObject(values)).then((res) => {
      setSavingMode(null)
      handleSubmitResult({ res, formik_funcs, on_ok })
    })
  }, [dispatch, subscription_id, customer_phone_number, subscription])

  const onSaveForwardingPhoneNumbers = ({ values, formik_funcs, onSaved }) => {
    if (!checkAreAllForwardingNumbersValid(values)) {
      return
    }
    setSavingMode('onSaveForwardingPhoneNumbers')

    customerPhoneNumberList.setForwardingPhoneNumbers({
      customer_phone_number,
      cluster_type: activeClusterType,
      ...values,
    })
    commonOnSavePhoneNumber({ formik_funcs, onSaved })
  }

  const checkAreAllForwardingNumbersValid = useCallback(({ values }) => {
    if (
      size(filter(forwardingPhoneNumberInfos, (x) => x.valid === false)) > 0
    ) {
      const text = <Trans>Some forwarding phone numbers are invalid.</Trans>
      dispatch(
        confirmModal({
          text: text,
          can_cancel: false,
          heading: <Trans>Validation error</Trans>,
          confirm_text: <Trans>Ok</Trans>,
          onConfirmed: () => {},
        })
      )
      return false
    } else {
      return true
    }
  }, [forwardingPhoneNumberInfos, dispatch])

  const onToggleIntroMessageEnabled = (clusterType) => {
    setSavingMode('onToggleIntroMessageEnabled')
    const is_intro_message_enabled =
        customerPhoneNumberList.getIsIntroMessageEnabled({customer_phone_number, cluster_type: clusterType})

    if (is_intro_message_enabled) {
      customerPhoneNumberList.disableIntroMessage({
        customer_phone_number,
        cluster_type: clusterType,
      })
    } else {
      customerPhoneNumberList.enableIntroMessage({
        customer_phone_number,
        cluster_type: clusterType,
      })
    }

    commonOnSavePhoneNumber({ customer_phone_number, formik_funcs: {} })
  }

  const onToggleGoodbyeMessageEnabled = (clusterType) => {
    setSavingMode('onToggleGoodbyeMessageEnabled')
    const is_goodbye_message_enabled =
        customerPhoneNumberList.getIsGoodbyeMessageEnabled({customer_phone_number, cluster_type: clusterType})

    if (is_goodbye_message_enabled) {
      customerPhoneNumberList.disableGoodbyeMessage({
        customer_phone_number,
        cluster_type: clusterType,
      })
    } else {
      customerPhoneNumberList.enableGoodbyeMessage({
        customer_phone_number,
        cluster_type: clusterType,
      })
    }

    commonOnSavePhoneNumber({ customer_phone_number, formik_funcs: {} })
  }
    
  const onToggleCallForwardingEnabled = () => {
    setSavingMode('onToggleCallForwardingEnabled')

    const is_call_forwarding_enabled =
      customerPhoneNumberList.isCallForwardingEnabled({
        customer_phone_number,
        cluster_type: activeClusterType,
      })

    if (is_call_forwarding_enabled) {
      customerPhoneNumberList.disableSendCallTo({
        customer_phone_number,
        cluster_type: activeClusterType,
      })
    } else {
      customerPhoneNumberList.enableSendCallTo({
        customer_phone_number,
        cluster_type: activeClusterType,
      })
    }

    commonOnSavePhoneNumber({ customer_phone_number, formik_funcs: {} })
  }

  const onToggleVoiceMailEnabled = () => {
    const on_ok = (json) => {
      // showSuccess(<Trans>Saved</Trans>)
    }

    setSavingMode('onToggleVoiceMailEnabled')
    const voice_mail_enabled = customerPhoneNumberList.getVoiceMailEnabled({
      customer_phone_number,
      cluster_type: activeClusterType,
    })

    dispatch(
      customerPhoneNumberList.setVoiceMailEnabled({
        customer_phone_number: customer_phone_number,
        cluster_type: activeClusterType,
        enabled: !voice_mail_enabled,
      })
    ).then((res) => {
      setSavingMode(null)
      handleSubmitResult({ res, formik_funcs: null, on_ok })
    })
  }

  const onToggleOfficeHoursEnabled = () => {
    const on_ok = (json) => {
      // showSuccess(<Trans>Saved</Trans>)
    }

    setSavingMode('onToggleOfficeHoursEnabled')

    dispatch(
      customerPhoneNumberList.setOfficeHoursEnabled({
        customer_phone_number: customer_phone_number,
        enabled: !office_hours_enabled,
      })
    ).then((res) => {
      setSavingMode(null)
      handleSubmitResult({ res, formik_funcs: null, on_ok })
    })
  }

  const onChangeForwardingListType = (value) => {
    customerPhoneNumberList.setForwardingListTypeOption({
      customer_phone_number,
      cluster_type: activeClusterType,
      forwarding_list_type: value,
    })

    setSavingMode('onChangeForwardingListType')
    commonOnSavePhoneNumber({
      customer_phone_number,
      formik_funcs: {},
      enableSuccessNotification: false,
    })
  }

  const onUpgradeSubscription = () => {
    history.push(`/subscription_upgrade_voice/${subscription.id}`)
  }

  const blockedNumberLookup = ({ partialPhoneNumber, onDone }) => {
    setBlockedNumberLookupOnDoneCallBack(() => onDone)
    dispatch(blockedNumberAutoCompleteList.lookupForNumber(partialPhoneNumber))
  }

  // ==== memoized derived props for rows ====
  const supportsBlockedNumbers = useMemo(
    () =>
      Boolean(
        get(subscription, 'product_supports_blocked_numbers', false)
      ),
    [subscription]
  )
  const isBlockedNumbersSaving = useMemo(
    () => savingMode === 'onSaveBlockedNumbers',
    [savingMode]
  )
  const lookupBlockedNumberCb = useCallback(
    ({ partialPhoneNumber, onDone }) =>
      blockedNumberLookup({ partialPhoneNumber, onDone }),
    [blockedNumberLookup]
  )

  const supportsIntroMessage = useMemo(
    () =>
      Boolean(
        get(subscription, 'product_supports_intro_message', false)
      ),
    [subscription]
  )

  const supportsGoodbyeMessage = useMemo(
    () =>
      Boolean(
        get(subscription, 'product_supports_goodbye_message', false)
      ),
    [subscription]
  )
    
  const supportsImmediateHangupMessage = useMemo(
    () =>
      Boolean(
        get(subscription, 'product_supports_immediate_hangup', false)
      ),
    [subscription]
  )
    
  const isForwardingEnabled = useMemo(
    () =>
      customerPhoneNumberList.isCallForwardingEnabled({
        customer_phone_number,
        cluster_type: activeClusterType,
      }),
    [customer_phone_number, activeClusterType]
  )
  const enabledDestinations = useMemo(
    () =>
      customerPhoneNumberList.getEnabledDestinations({
        customer_phone_number,
        cluster_type: activeClusterType,
        customer,
      }),
    [customer_phone_number, activeClusterType, customer]
  )
  const currentRingStrategy = useMemo(
    () =>
      customerPhoneNumberList.getRingStrategy({
        customer_phone_number,
        cluster_type: activeClusterType,
      }),
    [customer_phone_number, activeClusterType]
  )
  const currentCallerIdType = useMemo(
    () =>
      get(
        customerPhoneNumberList.getGlobalOptions({ customer_phone_number }),
        'caller_id_type',
        'caller'
      ),
    [customer_phone_number]
  )
  const getRateForNumberCb = useCallback(
    (number) =>
      callForwardingCostCalculatorList.getForNumber({
        subscription_id: subscription?.id,
        number,
      }),
    [subscription?.id]
  )
  const forwardingValidationMemo = useMemo(
    () => ({
      infos: forwardingPhoneNumberInfos,
      onBlur: onForwardingPhoneNumberBlur,
      onChange: onForwardingPhoneNumberChanged,
    }),
    [
      forwardingPhoneNumberInfos,
      onForwardingPhoneNumberBlur,
      onForwardingPhoneNumberChanged,
    ]
  )
  const isForwardingSaving = useMemo(
    () => savingMode === 'onSaveForwardingPhoneNumbers',
    [savingMode]
  )
  const isForwardingToggleSaving = useMemo(
    () => savingMode === 'onToggleCallForwardingEnabled',
    [savingMode]
  )
  const isForwardingListTypeSaving = useMemo(
    () => savingMode === 'onChangeForwardingListType',
    [savingMode]
  )
  const isIntroMessageSaving = useMemo(
    () => savingMode === 'onSaveIntroMessage'
    [savingMode]
  )

  const isIntroMessageEnabled = useMemo(
      () => customerPhoneNumberList.getIsIntroMessageEnabled({
      customer_phone_number,
      cluster_type: activeClusterType,
    }), [customer_phone_number, activeClusterType]
  )

  const isGoodbyeMessageSaving = useMemo(
    () => savingMode === 'onSaveGoodbyeMessage'
    [savingMode]
  )

  const isGoodbyeMessageEnabled = useMemo(
      () => customerPhoneNumberList.getIsGoodbyeMessageEnabled({
      customer_phone_number,
      cluster_type: activeClusterType,
    }), [customer_phone_number, activeClusterType]
  )
    
  const isVoiceMailEnabled = useMemo(
    () =>
      customerPhoneNumberList.getVoiceMailEnabled({
        customer_phone_number,
        cluster_type: activeClusterType,
      }),
    [customer_phone_number, activeClusterType]
  )
  const voiceMailInitialValues = useMemo(
    () =>
      Object.assign({}, customer_phone_number, {
        answering_message: customerPhoneNumberList.getActiveAnsweringMessage({
          customer_phone_number,
          cluster_type: activeClusterType,
        }),
        forward_voicemail_to_name:
          customerPhoneNumberList.getActiveVoiceForwardingName({
            customer_phone_number,
            cluster_type: activeClusterType,
          }) || '',
        forward_voicemail_to_email:
          customerPhoneNumberList.getActiveVoiceForwardingEmail({
            customer_phone_number,
            cluster_type: activeClusterType,
          }) || '',
      }),
    [customer_phone_number, activeClusterType]
  )
  const isVoiceMailSaving = useMemo(
    () => savingMode === 'onSavePhoneNumber',
    [savingMode]
  )
  const isVoiceMailToggleSaving = useMemo(
    () => savingMode === 'onToggleVoiceMailEnabled',
    [savingMode]
  )

  // ==== cluster models and actions (to keep JSX clean) ====

  const clusterModelsByType = useMemo(() => {
    const buildModel = (clusterType) => {
      const forwardingListType = customerPhoneNumberList.getForwardingListTypeOption({
        customer_phone_number,
        cluster_type: clusterType,
      })
      const isForwardingOn = customerPhoneNumberList.isCallForwardingEnabled({
        customer_phone_number,
        cluster_type: clusterType,
      })
      const destinations = customerPhoneNumberList.getEnabledDestinations({
        customer_phone_number,
        cluster_type: clusterType,
        customer,
      })
      const ringStrategy = customerPhoneNumberList.getRingStrategy({
        customer_phone_number,
        cluster_type: clusterType,
      })
      const voiceMailEnabled = customerPhoneNumberList.getVoiceMailEnabled({
        customer_phone_number,
        cluster_type: clusterType,
      })
      const voiceMailInitialValues = Object.assign({}, customer_phone_number, {
        answering_message: customerPhoneNumberList.getActiveAnsweringMessage({
          customer_phone_number,
          cluster_type: clusterType,
        }),
        forward_voicemail_to_name:
          customerPhoneNumberList.getActiveVoiceForwardingName({
            customer_phone_number,
            cluster_type: clusterType,
          }) || '',
        forward_voicemail_to_email:
          customerPhoneNumberList.getActiveVoiceForwardingEmail({
            customer_phone_number,
            cluster_type: clusterType,
          }) || '',
      })

      return {
        forwardingListType,
        isForwardingOn,
        destinations,
        ringStrategy,
        voiceMailEnabled,
        voiceMailInitialValues,
      }
    }

    return {
      default: buildModel('default'),
      in_office: buildModel('in_office'),
      out_of_office: buildModel('out_of_office'),
    }
  }, [customer_phone_number, customer])

  const clusterActionsByType = useMemo(() => {
    const buildActions = (clusterType) => {
      const onChangeType = (value) => {
        customerPhoneNumberList.setForwardingListTypeOption({
          customer_phone_number,
          cluster_type: clusterType,
          forwarding_list_type: value,
        })
        setSavingMode('onChangeForwardingListType')
        commonOnSavePhoneNumber({ formik_funcs: {}, enableSuccessNotification: false })
      }

      const onToggleForwarding = () => {
        setSavingMode('onToggleCallForwardingEnabled')
        const enabledNow = customerPhoneNumberList.isCallForwardingEnabled({
          customer_phone_number,
          cluster_type: clusterType,
        })
        if (enabledNow) {
          customerPhoneNumberList.disableSendCallTo({
            customer_phone_number,
            cluster_type: clusterType,
          })
        } else {
          customerPhoneNumberList.enableSendCallTo({
            customer_phone_number,
            cluster_type: clusterType,
          })
        }
        commonOnSavePhoneNumber({ formik_funcs: {} })
      }

      const onSaveForwarding = ({ values, formik_funcs, onSaved }) => {
        if (!checkAreAllForwardingNumbersValid(values)) {
          return
        }
        setSavingMode('onSaveForwardingPhoneNumbers')
        customerPhoneNumberList.setForwardingPhoneNumbers({
          customer_phone_number,
          cluster_type: clusterType,
          ...values,
        })
        commonOnSavePhoneNumber({ formik_funcs, onSaved })
      }

      const onToggleVoiceMail = () => {
        setSavingMode('onToggleVoiceMailEnabled')
        const current = customerPhoneNumberList.getVoiceMailEnabled({
          customer_phone_number,
          cluster_type: clusterType,
        })
        dispatch(
          customerPhoneNumberList.setVoiceMailEnabled({
            customer_phone_number,
            cluster_type: clusterType,
            enabled: !current,
          })
        ).then((res) => {
          setSavingMode(null)
          handleSubmitResult({ res, formik_funcs: null, on_ok: () => {} })
        })
      }

      const onSaveVoiceMail = ({ values, formik_funcs, onSaved }) => {
        setSavingMode('onSavePhoneNumber')
        const answering_message = values.answering_message
        if (answering_message) {
          customerPhoneNumberList.setActiveAnsweringMessage({
            customer_phone_number,
            cluster_type: clusterType,
            answering_message,
          })
        }
        customerPhoneNumberList.setActiveVoiceForwardingEmail({
          customer_phone_number,
          cluster_type: clusterType,
          forward_voicemail_to_email: get(values, 'forward_voicemail_to_email'),
        })
        customerPhoneNumberList.setActiveVoiceForwardingName({
          customer_phone_number,
          cluster_type: clusterType,
          forward_voicemail_to_name: get(values, 'forward_voicemail_to_name'),
        })
        commonOnSavePhoneNumber({ formik_funcs, onSaved })
      }

      const onSaveIntroMessage = ({ values, formik_funcs, onSaved }) => {
        setSavingMode('onSaveIntroMessage')
        const intro_message = values.intro_message
          if (intro_message) {
            customerPhoneNumberList.setIntroMessage({
              customer_phone_number,
              cluster_type: clusterType,
              intro_message,
            })
        }
        commonOnSavePhoneNumber({ formik_funcs, onSaved })
      }

      const onSaveGoodbyeMessage = ({ values, formik_funcs, onSaved }) => {
        setSavingMode('onSaveGoodbyeMessage')
        const goodbye_message = values.goodbye_message
          if (goodbye_message) {
            customerPhoneNumberList.setGoodbyeMessage({
              customer_phone_number,
              cluster_type: clusterType,
              goodbye_message,
            })
        }
        commonOnSavePhoneNumber({ formik_funcs, onSaved })
      }
        
        
      return {
        onChangeType,
        onToggleForwarding,
        onSaveForwarding,
        onToggleVoiceMail,
        onSaveVoiceMail,
        onSaveIntroMessage,
        onSaveGoodbyeMessage
      }
    }

    return {
      default: buildActions('default'),
      in_office: buildActions('in_office'),
      out_of_office: buildActions('out_of_office'),
    }
  }, [
    customer_phone_number,
    dispatch,
    setSavingMode,
    commonOnSavePhoneNumber,
    checkAreAllForwardingNumbersValid,
  ])

  const renderTitle = () => {
    const phone_number_name = get(customer_phone_number, 'phone_number_name')
    const has_phone_number_name = Boolean(phone_number_name)

    return (
      <Translation>
        {(t) => (
          <>
            <Row>
              <Col md={9} css={title_product_name_style}>
                {subscription.product_name}
                <Separator variant="h10" />
              </Col>
            </Row>
            <Row>
              <Col md={9}>
                <div css={title_style}>
                  <div css={title_style_label}>
                    <PhoneNumber
                      phone_number={subscription.phone_number_number}
                      enable_inline_spacing={true}
                    />
                    <Separator variant="w10" />
                    -
                    <Separator variant="w3" />
                  </div>
                  <div css={title_style_value}>
                    <InlineInputField
                      startInEditingMode={
                        get(customer_phone_number, 'id') &&
                        !has_phone_number_name
                      }
                      readOnlyValue={
                        <div
                          css={
                            has_phone_number_name
                              ? valid_customer_phone_name_style
                              : invalid_customer_phone_name_style
                          }
                        >
                          {phone_number_name || t('Enter a name')}
                        </div>
                      }
                      formInitialValues={phone_number_name_initial_values}
                      onSave={onSavePhoneNumberName}
                      is_saving={savingMode === 'onSavePhoneNumberName'}
                      validationSchema={phoneNumberNameValidationSchema}
                      renderFormFields={(formik_props) => (
                        <FormikInputField
                          name="phone_number_name"
                          input_css={phone_number_name_edit_style}
                          formik_props={formik_props}
                          placeholder="Enter a name"
                        />
                      )}
                    />
                  </div>
                </div>
              </Col>
            </Row>

            {subscription.monthly_num_free_voice_forwarding_minutes > 0 && (
              <Row>
                <Separator variant="h5" with_top_border={false} />
                <Col md={9} css={title_remaining_free_minutes_style}>
                  <FreeMinutesDuration
                    container_style={free_minutes_style}
                    minutes={
                      subscription.remaining_monthly_free_voice_forwarding_minutes
                    }
                  />
                  <Separator variant="w10" />
                  <div css={free_forwarding_post_tag_label_style}>
                    <Trans>free forwarding remaining this month</Trans>
                  </div>
                </Col>
              </Row>
            )}
          </>
        )}
      </Translation>
    )
  }
  

  const renderBackdoorRoutingJson = () => {
    return (
      <div css={backdoor_routing_style}>
        <h2>Routing information, for internal use only</h2>

        <h3>Synchronisation to Asterisk</h3>
        <PhoneNumberRedirectServerSyncLog
          customer_subscription_id={subscription_id}
        />

        <h3>Routing json</h3>
        <JSONPretty data={customer_phone_number.routing_as_json} />

        <h3>
          Routing json (as seen by gate, this can be different from the intended
          routing based on things like credits limits)
        </h3>
        <JSONPretty data={customer_phone_number.routing_as_json_for_asterisk} />
      </div>
    )
  }

  const renderCallForwardingSections = () => {
    const { applicable_days, start_time, end_time, timezone } =
      customerPhoneNumberList.getOfficeHours({ customer_phone_number })
    const office_hours_initial_values = {
      applicable_days: join(applicable_days, ','),
      start_time,
      end_time,
      timezone,
    }

    return (
      <>
        <h1>
          <Trans>Call Plan</Trans>
        </h1>

        <Separator variant="h30" />

        <div css={row_style}>
          <div css={toggle_column_style}>
            <NoodleHead />
          </div>
          <div css={value_column_style}>
            <h2 css={h2_style}>
              <Trans>When a call comes in</Trans>
            </h2>
          </div>
        </div>

        {get(window, ['LOCAL_SETTINGS', 'ENABLE_BLOCKED_NUMBERS'], false) && (
          <>
            <VoiceCallPlanBlockedNumbersRow
                supportsBlockedNumbers={supportsBlockedNumbers}
                blockedNumbers={blockedNumbersForSubscription}
                onSaveBlockedNumbers={onSaveBlockedNumbers}
                lookupBlockedNumber={lookupBlockedNumberCb}
                onUpgrade={onUpgradeSubscription}
                isSaving={isBlockedNumbersSaving}
                allBlockedNumbers={allBlockedNumbers}
            />
            <div css={noodle_row_style}>
              <div css={noodle_column_style}>
                <Separator variant="h30" with_top_border={false}>
                  <NoodleStem />
                </Separator>
              </div>
            </div>
          </>
        )}

        <VoiceCallPlanOfficeHoursRow
          enabled={office_hours_enabled}
          initialValues={office_hours_initial_values}
          onToggle={onToggleOfficeHoursEnabled}
          onSave={onSaveOfficeHours}
          officeHoursViewMode={officeHoursViewMode}
          setOfficeHoursViewMode={setOfficeHoursViewMode}
          isSaving={savingMode === 'onSaveOfficeHours'}
          isToggleSaving={savingMode === 'onToggleOfficeHoursEnabled'}
        />

        <div css={noodle_row_style}>
          <div css={noodle_column_style}>
            <Separator variant={office_hours_enabled ? "h10" : "h30"} with_top_border={false}>
              <NoodleStem />
            </Separator>
          </div>
        </div>

        {(() => {
          const renderClusterBlock = ({ clusterType, heading, withBackdoorToggle }) => {
            const model = clusterModelsByType[clusterType]
            const actions = clusterActionsByType[clusterType]
            const { forwardingListType, isForwardingOn, destinations, ringStrategy } = model
            const voiceMailEnabledForCluster = model.voiceMailEnabled
            const voiceMailInitialValuesForCluster = model.voiceMailInitialValues
            const { onChangeType, onToggleForwarding, onSaveForwarding, onToggleVoiceMail, onSaveVoiceMail, onSaveIntroMessage, onSaveGoodbyeMessage } = actions

            return (
              <div key={clusterType}>
                <div css={[inner_row_style, noodle_row_style]}>
                  <div css={non_toggle_column_style}>
                    <NoodleBendRight continueDown={clusterType === 'in_office'} />
                  </div>
                  <div css={inner_row_value_column_style}>
                    <NoodleHead />  
                  </div>
                  <div css={inner_row_value_column_style}>
                    <h2 css={h2_style}>{heading}</h2>
                  </div>
                </div>

                <div css={send_call_to_row_with_offset_style}>
                  <div css={[indented_noodle_row_style, indented_noodle_column_style, indented_noodle_column_positioned_style]}>
                    {clusterType === 'in_office' && (
                      <div css={noodle_stem_fill_parent_style}>
                        <NoodleStem />
                      </div>
                    )}
                  </div>

                  <div style={{ width: '100%' }}>

                    <VoiceCallPlanIntroMessageRow canEdit={supportsIntroMessage}
                                                  onToggle={onToggleIntroMessageEnabled}
                                                  isToggleSaving={savingMode === 'onToggleIntroMessageEnabled'}
                                                  onSave={onSaveIntroMessage}
                                                  isSaving={savingMode === 'onSaveIntroMessage'}
                                                  introMessage={customerPhoneNumberList.getIntroMessage({customer_phone_number, cluster_type: clusterType})}
                                                  isEnabled={customerPhoneNumberList.getIsIntroMessageEnabled({customer_phone_number, cluster_type: clusterType})}
                                                  overrideAnsweringIntroMessageList={overrideAnsweringIntroMessageList}
                                                  customer_phone_number={customer_phone_number}
                                                  clusterType={clusterType}
                                                  onUpgrade={onUpgradeSubscription}
                    />

                    <div css={noodle_row_style}>
                      <div css={noodle_column_style}>
                        <Separator variant="h30" with_top_border={false}>
                          <NoodleStem />
                        </Separator>
                      </div>
                    </div>
                    
                    <VoiceCallPlanForwardingRow
                      clusterType={clusterType}
                      forwardingListType={forwardingListType}
                      onChangeType={onChangeType}
                      isEnabled={isForwardingOn}
                      onToggle={onToggleForwarding}
                      destinations={destinations}
                      ringStrategy={ringStrategy}
                      callerIdType={currentCallerIdType}
                      onSave={onSaveForwarding}
                      forwardingValidation={forwardingValidationMemo}
                      getRateForNumber={getRateForNumberCb}
                      subscription={subscription}
                      customer={customer}
                      customer_phone_number={customer_phone_number}
                      isSaving={isForwardingSaving}
                      isToggleSaving={isForwardingToggleSaving}
                      isListTypeSaving={isForwardingListTypeSaving}
                      editing_as_customer={editing_as_customer}
                      onBuyCredit={onBuyCredit}
                    />

                    <div css={noodle_row_style}>
                      <div css={noodle_column_style}>
                        <Separator variant="h30" with_top_border={false}>
                          <NoodleStem />
                        </Separator>
                      </div>
                    </div>

                    <VoiceCallPlanVoiceMailRow
                      isEnabled={voiceMailEnabledForCluster}
                      onToggle={onToggleVoiceMail}
                      initialValues={voiceMailInitialValuesForCluster}
                      onSave={onSaveVoiceMail}
                      greetingSelectorVisible={greetingSelectorVisible}
                      setGreetingSelectorVisible={setGreetingSelectorVisible}
                      overrideAnsweringMessageList={overrideAnsweringVoiceMessageList}
                      isSaving={isVoiceMailSaving}
                      isToggleSaving={isVoiceMailToggleSaving}
                      subscription={subscription}
                      customer_phone_number={customer_phone_number}
                    />

                    <div css={noodle_row_style}>
                      <div css={noodle_column_style}>
                        <Separator variant="h30" with_top_border={false}>
                          <NoodleStem />
                        </Separator>
                      </div>
                    </div>
                
                    <VoiceCallPlanGoodbyeMessageRow canEdit={supportsGoodbyeMessage}
                                                  onToggle={onToggleGoodbyeMessageEnabled}
                                                  isToggleSaving={savingMode === 'onToggleGoodbyeMessageEnabled'}
                                                  onSave={onSaveGoodbyeMessage}
                                                  isSaving={savingMode === 'onSaveGoodbyeMessage'}
                                                  goodbyeMessage={customerPhoneNumberList.getGoodbyeMessage({customer_phone_number, cluster_type: clusterType})}
                                                  isEnabled={customerPhoneNumberList.getIsGoodbyeMessageEnabled({customer_phone_number, cluster_type: clusterType})}
                                                  overrideAnsweringGoodbyeMessageList={overrideAnsweringGoodbyeMessageList}
                                                  customer_phone_number={customer_phone_number}
                                                  clusterType={clusterType}
                                                  onUpgrade={onUpgradeSubscription}
                    />
                
                    <div css={noodle_row_style}>
                      <div
                        css={noodle_column_style}
                        onDoubleClick={withBackdoorToggle ? () => setBackdoorShowRoutingJson(!backdoorShowRoutingJson) : undefined}
                      >
                        <Separator variant="h50" with_top_border={false}>
                          <NoodleTail />
                        </Separator>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            )
          }

          if (office_hours_enabled) {
            return (
              <>
                {renderClusterBlock({
                  clusterType: 'in_office',
                  heading: <Trans>During work hours</Trans>,
                  withBackdoorToggle: false,
                })}
                <div css={noodle_row_style}>
                  <div css={noodle_column_style}>
                    <Separator variant="h30" with_top_border={false}>
                      <NoodleStem />
                    </Separator>
                  </div>
                </div>
                {renderClusterBlock({
                  clusterType: 'out_of_office',
                  heading: <Trans>Outside work hours</Trans>,
                  withBackdoorToggle: true,
                })}
              </>
            )
          }

          const actions = clusterActionsByType[activeClusterType]
          const { onSaveIntroMessage, onSaveGoodbyeMessage } = actions
              
          return (
            <div>
              <div css={office_hours_enabled ? send_call_to_row_with_offset_style : null}>

                <VoiceCallPlanIntroMessageRow canEdit={supportsIntroMessage}
                                              onToggle={onToggleIntroMessageEnabled}
                                              isToggleSaving={savingMode === 'onToggleIntroMessageEnabled'}
                                              onSave={onSaveIntroMessage}
                                              isSaving={savingMode === 'onSaveIntroMessage'}
                                              introMessage={customerPhoneNumberList.getIntroMessage({customer_phone_number, cluster_type: activeClusterType})}
                                              isEnabled={customerPhoneNumberList.getIsIntroMessageEnabled({customer_phone_number, cluster_type: activeClusterType})}
                                              overrideAnsweringIntroMessageList={overrideAnsweringIntroMessageList}
                                              customer_phone_number={customer_phone_number}
                                              clusterType={activeClusterType}
                                              onUpgrade={onUpgradeSubscription}
                />

                <div css={noodle_row_style}>
                  <div css={noodle_column_style}>
                    <Separator variant="h30" with_top_border={false}>
                      <NoodleStem />
                    </Separator>
                  </div>
                </div>
                
                <VoiceCallPlanForwardingRow
                  clusterType={activeClusterType}
                  forwardingListType={forwardingListTypeOption}
                  onChangeType={onChangeForwardingListType}
                  isEnabled={isForwardingEnabled}
                  onToggle={onToggleCallForwardingEnabled}
                  destinations={enabledDestinations}
                  ringStrategy={currentRingStrategy}
                  callerIdType={currentCallerIdType}
                  onSave={onSaveForwardingPhoneNumbers}
                  forwardingValidation={forwardingValidationMemo}
                  getRateForNumber={getRateForNumberCb}
                  subscription={subscription}
                  customer={customer}
                  customer_phone_number={customer_phone_number}
                  isSaving={isForwardingSaving}
                  isToggleSaving={isForwardingToggleSaving}
                  isListTypeSaving={isForwardingListTypeSaving}
                  editing_as_customer={editing_as_customer}
                  onBuyCredit={onBuyCredit}
                />

                <div css={noodle_row_style}>
                  <div css={noodle_column_style}>
                    <Separator variant="h30" with_top_border={false}>
                      <NoodleStem />
                    </Separator>
                  </div>
                </div>

                <VoiceCallPlanVoiceMailRow
                  isEnabled={isVoiceMailEnabled}
                  onToggle={onToggleVoiceMailEnabled}
                  initialValues={voiceMailInitialValues}
                  onSave={onSavePhoneNumber}
                  greetingSelectorVisible={greetingSelectorVisible}
                  setGreetingSelectorVisible={setGreetingSelectorVisible}
                  overrideAnsweringMessageList={overrideAnsweringVoiceMessageList}
                  isSaving={isVoiceMailSaving}
                  isToggleSaving={isVoiceMailToggleSaving}
                  subscription={subscription}
                  customer_phone_number={customer_phone_number}
                />

                <div css={noodle_row_style}>
                  <div css={noodle_column_style}>
                    <Separator variant="h30" with_top_border={false}>
                      <NoodleStem />
                    </Separator>
                  </div>
                </div>
              
                <VoiceCallPlanGoodbyeMessageRow canEdit={supportsGoodbyeMessage}
                                              onToggle={onToggleGoodbyeMessageEnabled}
                                              isToggleSaving={savingMode === 'onToggleGoodbyeMessageEnabled'}
                                              onSave={onSaveGoodbyeMessage}
                                              isSaving={savingMode === 'onSaveGoodbyeMessage'}
                                              goodbyeMessage={customerPhoneNumberList.getGoodbyeMessage({customer_phone_number, cluster_type: activeClusterType})}
                                              isEnabled={customerPhoneNumberList.getIsGoodbyeMessageEnabled({customer_phone_number, cluster_type: activeClusterType})}
                                              overrideAnsweringGoodbyeMessageList={overrideAnsweringGoodbyeMessageList}
                                              customer_phone_number={customer_phone_number}
                                              clusterType={activeClusterType}
                                              onUpgrade={onUpgradeSubscription}
                />
                
                <div css={noodle_row_style}>
                  <div
                    css={noodle_column_style}
                    onDoubleClick={() =>
                      setBackdoorShowRoutingJson(!backdoorShowRoutingJson)
                    }
                  >
                    <Separator variant="h50" with_top_border={false}>
                      <NoodleTail />
                    </Separator>
                  </div>
                </div>
              </div>
            </div>
          )
        })()}
        {backdoorShowRoutingJson && renderBackdoorRoutingJson()}
      </>
    )
  }

  const renderUneditable = () => {
    return (
      <>
        <CardHeader title={<Trans>Under construction</Trans>} />

        <Card variant="white">
          <div css={sub_heading_style}>
            <Trans>Please note</Trans>
          </div>
          <Separator variant="h10" />

          <Trans
            i18nKey="voice_numbers_under_construction_p1"
            values={{
              helpdesk_email: helpdesk_email,
              helpdesk_phone: helpdesk_phone,
            }}
            components={[
              <a css={helpdesk_contact_style} href={`mailto:${helpdesk_email}`}>
                {helpdesk_email}
              </a>,
              <a css={helpdesk_contact_style} href={`tel:${helpdesk_phone}`}>
                {helpdesk_phone}
              </a>,
            ]}
          />
          <Separator variant="h10" />
          <Trans
            i18nKey="voice_numbers_under_construction_p2"
            values={{
              helpdesk_email: helpdesk_email,
              helpdesk_phone: helpdesk_phone,
            }}
            components={[
              <a css={helpdesk_contact_style} href={`mailto:${helpdesk_email}`}>
                {helpdesk_email}
              </a>,
              <a css={helpdesk_contact_style} href={`tel:${helpdesk_phone}`}>
                {helpdesk_phone}
              </a>,
            ]}
          />
          <Separator variant="h10" />
          <Trans
            i18nKey="voice_numbers_under_construction_p3"
            values={{
              helpdesk_email: helpdesk_email,
              helpdesk_phone: helpdesk_phone,
            }}
            components={[
              <a css={helpdesk_contact_style} href={`mailto:${helpdesk_email}`}>
                {helpdesk_email}
              </a>,
              <a css={helpdesk_contact_style} href={`tel:${helpdesk_phone}`}>
                {helpdesk_phone}
              </a>,
            ]}
          />
        </Card>
      </>
    )
  }

  return (
    <WrappingBusyMask is_loading={is_loading}>
      {renderTitle()}
      <Separator variant="h50" />
      <Row>
        <>
          { ! subscription.is_active &&
            <Error>
              <Trans>This subscription is not active.</Trans>
            </Error>
          }

          { subscription.is_active && 
            <Col md={12}>
              {customerPhoneNumberList.isEditable({ customer_phone_number }) &&
               renderCallForwardingSections()}
              {!customerPhoneNumberList.isEditable({ customer_phone_number }) &&
               renderUneditable()}
            </Col>
          }
        </>
      </Row>
    </WrappingBusyMask>
  )
}

const title_style = css`
  display: flex;
  flex-wrap: wrap;
  font-size: 28px;
  font-weight: bold;
  align-items: center;
`

const title_style_label = css`
  display: flex;
  align-items: center;
`

const title_style_value = css`
  flex-grow: 1;
  max-width: 600px;
`

const row_style = css`
  display: flex;
  flex-wrap: wrap;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 15px;
  width: 100%;
  margin-left: 0px;
  margin-right: 30px;
  align-items: center;
  min-height: 68px;
`

const inner_row_style = css`
  display: flex;
  padding-left: 20px;
  padding-right: 20px;
  margin-left: 0px;
  margin-right: 30px;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
`

const h2_style = css`
  font-size: 20px;
`

const toggle_column_style = css`
  display: flex;
  margin-right: 15px;
  min-width: 40px;
  max-width: 40px;
  align-self: flex-start;
  margin-top: 8px;
  justify-content: center;
  width: 100%;
`

const non_toggle_column_style = css`
  display: flex;
  margin-left: 17px;
  min-width: 70px;
  max-width: 70px;
  align-self: flex-start;
  justify-content: flex-start;
  height: 70px;
`

const value_column_style = css`
  padding-bottom: 15px;
  flex-grow: 1;
`

const inner_row_value_column_style = css`
  margin-top: 23px;
  margin-right: 20px;
`

const valid_customer_phone_name_style = css``

const invalid_customer_phone_name_style = css`
  color: ${theme.colors.dark_grey};
`

const helpdesk_contact_style = css``

const sub_heading_style = css`
  font-weight: bold;
  font-size: 18px;
`

const noodle_column_style = css`
  width: 80px;
  max-width: 80px;
  min-width: 80px;
`

const indented_noodle_column_style = css`
  width: 40px;
  max-width: 40px;
  min-width: 40px;
  display: flex;
  flex-direction: column;
  align-items: start;
  align-self: stretch;
  justify-content: start;
`

const noodle_row_style = css`
  margin-left: 0px;
  margin-right: 30px;
  width: 100%;
  
`

const indented_noodle_row_style = css`
  margin-left: 40px;
  width: 100%;
`

const indented_noodle_column_positioned_style = css`
  position: relative;
`

const noodle_stem_fill_parent_style = css`
  position: absolute;
  left: 0;
  transform: translateX(-50%);
  top: 0;
  bottom: 0;
  height: auto;
`

const phone_number_name_edit_style = css`
  width: 400px;
`

const backdoor_routing_style = css`
  margin-top: 30px;
  border: 1px solid ${theme.colors.dark_middle_grey};
  padding-top: 30px;
  background-color: ${theme.colors.very_light_grey};
  padding: 10px;
`


const free_minutes_style = css`
  font-size: 13px;
  padding: 3px 9px 5px 8px;
`

const title_product_name_style = css`
  font-weight: bold;
`

const title_remaining_free_minutes_style = css`
  font-weight: bold;
  display: flex;

`

const free_forwarding_post_tag_label_style = css`
padding-top: 4px;
`

const send_call_to_row_with_offset_style = css`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: flex-start;
  height: auto;
`
